/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 157 lines
 * - Adding: ~5 lines
 * - Projected: 162 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts, auth pages
 */

import { FunctionComponent, useState } from "react";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { requestPasswordReset } from "../../services/usersService";
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-01-XX
 * Modified By:  Cursor
 *
 * Description:
 * Replaced direct toast.* calls with centralized notify helper for consistent messaging.
 *
 * Related Screens / Modules:
 * - client/src/pages/auth/ForgotPassword.tsx
 * - client/src/utils/notifications.ts
 */

import { notify } from "../../utils/notifications";
import * as yup from "yup";

interface ForgotPasswordFormValues {
  email: string;
}

const forgotPasswordValidationSchema = yup.object({
  email: yup.string().required("Email is required").email("Please enter a valid email"),
});

const ForgotPassword: FunctionComponent = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const formik = useFormik<ForgotPasswordFormValues>({
    initialValues: {
      email: "",
    },
    validationSchema: forgotPasswordValidationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        await requestPasswordReset(values.email);
        setIsSubmitted(true);
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.success("We've sent you a password reset link. Please check your inbox");
      } catch (err: any) {
        console.error("Password reset request error:", err);

        // Handle network errors (server not reachable)
        if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error(err, "We can't connect right now. Please check your internet connection and try again");
          return;
        }

        // Handle other HTTP errors
        let errorMessage = "We couldn't send the reset email. Please try again or contact support if the problem continues";
        if (err.response?.data) {
          if (typeof err.response.data === "string") {
            errorMessage = err.response.data;
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          } else if (err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }

        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(err, errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (isSubmitted) {
    return (
      <div className="container d-flex justify-content-center align-items-center auth-page-min-height">
        <div className="card auth-card-max-width">
          <div className="card-body p-4">
            <h2 className="signin-title mb-3">Check Your Email</h2>
            <div className="alert alert-success" role="alert">
              <p className="mb-2">
                We've sent a password reset link to <strong>{formik.values.email}</strong>
              </p>
              <p className="mb-0">
                Please check your email and click the link to reset your password.
              </p>
            </div>
            <div className="text-center mt-4">
              <button onClick={() => navigate("/?showLogin=true")} className="btn-black">
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container d-flex justify-content-center align-items-center auth-fullheight">
      <div className="card auth-card-max-width">
        <div className="card-body p-4">
          <h2 className="signin-title mb-3">Forgot Password?</h2>
          <p className="text-muted mb-4">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          <form onSubmit={formik.handleSubmit}>
            <div className="mb-4">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                type="email"
                className="form-control email-input auth-text-18"
                placeholder="Enter your email"
                name="email"
                id="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isLoading}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-danger mt-1 error-text-12">{formik.errors.email}</div>
              )}
            </div>

            <button
              type="submit"
              className="btn-black w-100 mb-4"
              disabled={isLoading || !formik.isValid}
            >
              <span className="signin-button-text">
                {isLoading ? "Sending..." : "Send Reset Link"}
              </span>
            </button>
          </form>

          <div className="text-center mt-3">
            <Link to="/" className="text-decoration-none">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
